/*
 * How one build of the console hands the page to the next without a reload.
 * The TV board takes a new deploy that way while it is full screen (fresh.tsx):
 * a reload left full screen, and only a click can enter it again — the owner
 * found the meeting room's screen out of full screen after deploys (2026-10-20).
 *
 * The next build draws into a box this build makes for it, named on the window
 * (the one thing two builds share), and this build undoes what it set up — its
 * React, its sound — once the next one is on screen.
 */

const NEXT = "__laziorNextMount";
type Handover = Window & { [NEXT]?: HTMLElement };

/** Where this build draws: the box the build before it made, or #root. */
export function mountPoint(): HTMLElement {
  const w = window as Handover;
  const box = w[NEXT];
  delete w[NEXT];
  const el = box ?? document.getElementById("root");
  if (!el) throw new Error("#root element is missing from index.html");
  return el;
}

/** Names the box the next build will draw into; null takes it back. */
export function mountNextIn(box: HTMLElement | null): void {
  const w = window as Handover;
  if (box) w[NEXT] = box;
  else delete w[NEXT];
}

let leaving: (() => void)[] = [];

/** Something this build undoes when the next build takes the page. */
export function onLeave(undo: () => void): void {
  leaving.push(undo);
}

/**
 * This build's undoing, taken before the next build loads: a file both builds
 * share (same name, same hash) is one module, and what the next build adds to
 * it is not this build's to undo. `giveBack` is for a next build that never came.
 */
export function takeLeaving(): { run: () => void; giveBack: () => void } {
  const mine = leaving;
  leaving = [];
  return {
    run: () =>
      mine.forEach((undo) => {
        try {
          undo();
        } catch {
          /* the rest still go */
        }
      }),
    giveBack: () => {
      leaving = [...mine, ...leaving];
    },
  };
}
